import { call, put, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
//import { noop } from 'utils/common';

import {
    createOQClientSuccess,
    createOQClientError,
    createOQEmployeeSuccess,
    createOQEmployeeError,
    deleteOQClientSuccess,
    deleteOQClientError,
    deleteOQEmployeeSuccess,
    deleteOQEmployeeError,
    getOQProfile,
    getOQProfileSuccess,
    getOQProfileError,
    getOQClient,
    getOQClientSuccess,
    getOQClientError,
    getOQAdministrationURLSuccess,
    getOQAdministrationURLError,
    sendOQAdministrationSMSSuccess,
    sendOQAdministrationSMSError,
    sendOQAdministrationEmailSuccess,
    sendOQAdministrationEmailError,
    getOQScheduleSuccess,
    getOQScheduleError,
    setOQScheduleSuccess,
    setOQScheduleError,
    stopOQScheduleSuccess,
    stopOQScheduleError,
    getPatientDeviceStatusSuccess,
    getPatientDeviceStatusError,
    sendOQAdministrationPushSuccess,
    sendOQAdministrationPushError,
} from './actions';
import {
    CREATE_OQ_CLIENT,
    CREATE_OQ_EMPLOYEE,
    DELETE_OQ_CLIENT,
    DELETE_OQ_EMPLOYEE,
    GET_OQ_ADMINISTRATION_URL,
    GET_OQ_CLIENT,
    GET_OQ_PROFILE,
    SEND_OQ_ADMINISTRATION_SMS,
    SEND_OQ_ADMINISTRATION_EMAIL,
    GET_OQ_SCHEDULE,
    SET_OQ_SCHEDULE,
    STOP_OQ_SCHEDULE,
    GET_PATIENT_DEVICE_STATUS,
    SEND_OQ_ADMINISTRATION_PUSH,
} from './constants';
import {
    CreateOQClientAction,
    CreateOQEmployeeAction,
    DeleteOQClientAction,
    DeleteOQClientResponse,
    DeleteOQEmployeeAction,
    DeleteOQEmployeeResponse,
    GetOQProfileAction,
    GetOQClientAction,
    GetOQAdministrationURLAction,
    SendOQAdministrationSMSAction,
    SendOQAdministrationEmailAction,
    APIError,
    CreateOQClientResponse,
    CreateOQEmployeeResponse,
    GetOQProfileResponse,
    GetOQClientResponse,
    GetOQAdministrationURLResponse,
    SendOQAdministrationSMSResponse,
    SendOQAdministrationEmailResponse,
    GetOQScheduleAction,
    GetOQScheduleResponse,
    SetOQScheduleAction,
    SetOQScheduleResponse,
    StopOQScheduleAction,
    StopOQScheduleResponse,
    GetPatientDeviceStatusAction,
    GetPatientDeviceStatusResponse,
    SendOQAdministrationPushAction,
    SendOQAdministrationPushResponse,
} from './types';
import { loadAllClinicians } from 'containers/CliniciansAndClients/actions';
import {
    createOQClientAPI,
    createOQEmployeeAPI,
    deleteOQClientAPI,
    deleteOQEmployeeAPI,
    getOQProfileAPI,
    getOQClientAPI,
    getOQAdministrationURLAPI,
    sendOQAdministrationSMSAPI,
    getPatientDeviceStatusAPI,
    sendOQAdministrationPushAPI,
    sendOQAdministrationEmailAPI,
    getOQScheduleAPI,
    setOQScheduleAPI,
    stopOQScheduleAPI,
} from 'utils/api';

import { getLogger } from 'utils/logger';
const logger = getLogger('v2.measures.saga');

const handleError = (error: unknown, functionName: string): string => {
    if (error instanceof Error) {
        logger.error(error.message); // only logger.error gets printed in prod
        logger.debug(error.cause); // don't expose stack trace in production
        return error.message;
    }
    const message = 'Unknown error has occurred';
    logger.error(message);
    logger.debug(functionName + ' error: ');
    logger.debug(error); // attempt to log error
    return message;
};

function* createOQClientSaga(action: CreateOQClientAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(createOQClientAPI, data)) as unknown as CreateOQClientResponse;
        if ('data' in response && response.data) {
            yield put(createOQClientSuccess(response.data));
            yield put(getOQClient({ patientId: data.patientId }));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'createOQClientSaga');
        yield put(createOQClientError(message));
    }
}

function* createOQEmployeeSaga(action: CreateOQEmployeeAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(createOQEmployeeAPI, data)) as unknown as CreateOQEmployeeResponse;
        if ('data' in response && response.data) {
            yield put(createOQEmployeeSuccess(response.data));
            yield put(getOQProfile({ userId: data.employeeId }));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'createOQEmployeeSaga');
        yield put(createOQEmployeeError(message));
    }
}

function* deleteOQClientSaga(action: DeleteOQClientAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(deleteOQClientAPI, data)) as unknown as DeleteOQClientResponse;
        if ('data' in response && response.data) {
            yield put(deleteOQClientSuccess({ patientId: data.patientId, result: response.data }));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'deleteOQClientSaga');
        yield put(deleteOQClientError(message));
    }
}

function* deleteOQEmployeeSaga(action: DeleteOQEmployeeAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(deleteOQEmployeeAPI, data)) as unknown as DeleteOQEmployeeResponse;
        if ('data' in response && response.data) {
            yield put(deleteOQEmployeeSuccess({
                employeeId: data.employeeId,
                result: response.data,
            }));
            // Refresh clinicians list so admin-driven deletions update hasOqAccount flags.
            yield put(loadAllClinicians());
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'deleteOQEmployeeSaga');
        yield put(deleteOQEmployeeError(message));
    }
}

function* getOQProfileSaga(action: GetOQProfileAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(getOQProfileAPI, data)) as unknown as GetOQProfileResponse;
        if ('data' in response && response.data) {
            yield put(getOQProfileSuccess(response.data));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'getOQProfileSaga');
        yield put(getOQProfileError(message));
    }
}

function* getOQClientSaga(action: GetOQClientAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(getOQClientAPI, data)) as unknown as GetOQClientResponse;
        if (!('data' in response) || !response.data) {
            if ('response' in response) {
                const error: APIError = response.response.data;
                throw Error(error.error, { cause: error });
            }
        } else if (response.data.success) {
            yield put(getOQClientSuccess(response.data.data));
        }
    } catch (e) {
        const message = handleError(e, 'getOQClientSaga');
        yield put(getOQClientError(message));
    }
}

function* getOQAdministrationURLSaga(action: GetOQAdministrationURLAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(getOQAdministrationURLAPI, data)) as unknown as GetOQAdministrationURLResponse;
        if ('data' in response && response.data) {
            yield put(getOQAdministrationURLSuccess(response.data.data.url));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'getOQAdministrationURLSaga');
        yield put(getOQAdministrationURLError(message));
    }
}

function* sendOQAdministrationSMSSaga(action: SendOQAdministrationSMSAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(sendOQAdministrationSMSAPI, data)) as unknown as SendOQAdministrationSMSResponse;
        if ('data' in response && response.data) {
            yield put(sendOQAdministrationSMSSuccess(response.data));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'sendOQAdministrationSMSSaga');
        yield put(sendOQAdministrationSMSError(message));
    }
}

// Exported for its unit test (saga.sendEmail.test.ts): the failure mode worth
// pinning is a copy-paste one — calling the SMS endpoint from the email send.
export function* sendOQAdministrationEmailSaga(action: SendOQAdministrationEmailAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(sendOQAdministrationEmailAPI, data)) as unknown as SendOQAdministrationEmailResponse;
        if ('data' in response && response.data) {
            yield put(sendOQAdministrationEmailSuccess(response.data));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'sendOQAdministrationEmailSaga');
        yield put(sendOQAdministrationEmailError(message));
    }
}

function* getOQScheduleSaga(action: GetOQScheduleAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(getOQScheduleAPI, data)) as unknown as GetOQScheduleResponse;
        if ('data' in response && response.data) {
            yield put(getOQScheduleSuccess(response.data.schedule));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'getOQScheduleSaga');
        yield put(getOQScheduleError(message));
    }
}

function* setOQScheduleSaga(action: SetOQScheduleAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(setOQScheduleAPI, data)) as unknown as SetOQScheduleResponse;
        if ('data' in response && response.data) {
            yield put(setOQScheduleSuccess(response.data.schedule));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'setOQScheduleSaga');
        yield put(setOQScheduleError(message));
    }
}

function* stopOQScheduleSaga(action: StopOQScheduleAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(stopOQScheduleAPI, data)) as unknown as StopOQScheduleResponse;
        if ('data' in response && response.data) {
            yield put(stopOQScheduleSuccess());
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'stopOQScheduleSaga');
        yield put(stopOQScheduleError(message));
    }
}

function* getPatientDeviceStatusSaga(action: GetPatientDeviceStatusAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(getPatientDeviceStatusAPI, data)) as unknown as GetPatientDeviceStatusResponse;
        if ('data' in response && response.data) {
            yield put(getPatientDeviceStatusSuccess(response.data));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'getPatientDeviceStatusSaga');
        yield put(getPatientDeviceStatusError(message));
    }
}

function* sendOQAdministrationPushSaga(action: SendOQAdministrationPushAction): SagaIterator {
    const { data } = action;
    try {
        const response = (yield call(sendOQAdministrationPushAPI, data)) as unknown as SendOQAdministrationPushResponse;
        if ('data' in response && response.data) {
            yield put(sendOQAdministrationPushSuccess(response.data));
        } else if ('response' in response) {
            const error: APIError = response.response.data;
            throw Error(error.error, { cause: error });
        }
    } catch (e) {
        const message = handleError(e, 'sendOQAdministrationPushSaga');
        yield put(sendOQAdministrationPushError(message));
    }
}

// Define the root saga
export default function* measuresSaga(): SagaIterator {
    yield takeLatest(CREATE_OQ_CLIENT, createOQClientSaga);
    yield takeLatest(CREATE_OQ_EMPLOYEE, createOQEmployeeSaga);
    yield takeLatest(DELETE_OQ_CLIENT, deleteOQClientSaga);
    yield takeLatest(DELETE_OQ_EMPLOYEE, deleteOQEmployeeSaga);
    yield takeLatest(GET_OQ_PROFILE, getOQProfileSaga);
    yield takeLatest(GET_OQ_CLIENT, getOQClientSaga);
    yield takeLatest(GET_OQ_ADMINISTRATION_URL, getOQAdministrationURLSaga);
    yield takeLatest(SEND_OQ_ADMINISTRATION_SMS, sendOQAdministrationSMSSaga);
    yield takeLatest(SEND_OQ_ADMINISTRATION_EMAIL, sendOQAdministrationEmailSaga);
    yield takeLatest(GET_OQ_SCHEDULE, getOQScheduleSaga);
    yield takeLatest(SET_OQ_SCHEDULE, setOQScheduleSaga);
    yield takeLatest(STOP_OQ_SCHEDULE, stopOQScheduleSaga);
    yield takeLatest(GET_PATIENT_DEVICE_STATUS, getPatientDeviceStatusSaga);
    yield takeLatest(SEND_OQ_ADMINISTRATION_PUSH, sendOQAdministrationPushSaga);
}
